import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Truck, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Logo';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { LanguageToggle, useT } from '../utils/i18n';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setMobileAboutOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  const navLinkClass = 'text-stone-800 dark:text-white';
  const dropdownLinkClass = 'text-stone-600 dark:text-stone-300';
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-brand-muted';
  const {
    totalItems,
    totalPrice,
    toggleCart
  } = useCart();
  const t = useT();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  const goToProduct = useCallback((categoryName: string, productIndex: number) => {
    const params = new URLSearchParams({
      cat: categoryName,
      prod: String(productIndex)
    });
    if (location.pathname !== '/') {
      navigate(`/?${params.toString()}#materials`);
    } else {
      window.history.replaceState(null, '', `/?${params.toString()}#materials`);
      document.getElementById('materials')?.scrollIntoView({
        behavior: 'smooth'
      });
      // Dispatch event so Services picks up the change
      window.dispatchEvent(new Event('materialsNav'));
    }
  }, [location.pathname, navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? 'bg-white dark:bg-black backdrop-blur-md shadow-lg border-stone-200 dark:border-white/20' : 'bg-white dark:bg-black/75 backdrop-blur-md border-stone-200 dark:border-white/20'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? '56px' : isMobileNav ? '80px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-919">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-920">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? '56px' : isMobileNav ? '80px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-921">
          <div className="flex-shrink-0 flex items-center" data-id="element-922">
            <Link to="/" onClick={resetMaterials} className="relative" data-id="element-923">
              <motion.div className="origin-left" initial={{
              opacity: 0,
              scale: 1
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              opacity: {
                duration: 2,
                ease: 'easeOut'
              }
            }} data-id="element-924">
                <img src="https://cdn.magicpatterns.com/uploads/3C1xrkGTfVHurUv4Wdvj7g/logo_253.png" alt="253Gravel" className="h-10 max-sm:h-7 w-auto dark:brightness-100 brightness-0" data-id="element-925" />
              </motion.div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8" data-id="element-926">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-927">
              <button className={`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`} data-id="element-928">
                {t('nav.shop')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-929" />
              </button>

              <AnimatePresence data-id="element-930">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-931">
                    <div className="grid grid-cols-3 gap-5" data-id="element-932">
                      {categories.map(cat => <div key={cat.name} data-id="element-933">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-934">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-935">
                            {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(cat.name, prodIdx);
                      }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors text-left w-full`} data-id="element-936">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 pt-4 border-t border-stone-200 dark:border-white/10" data-id="element-937">
                      <Link to="/subcontractors" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors group" data-id="element-938">
                        <div data-id="element-939">
                          <span className="block text-brand-orange font-bold text-sm" data-id="element-940">
                            Contractor Ordering
                          </span>
                          <span className="block text-stone-600 dark:text-stone-200 text-xs mt-0.5" data-id="element-941">
                            Bulk pricing & volume discounts
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-brand-orange opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-942" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Help Me Choose Dropdown */}
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-943">
              <button className={`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`} data-id="element-944">
                {t('nav.helpMeChoose')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-945" />
              </button>
              <AnimatePresence data-id="element-946">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-2 w-56 bg-white dark:bg-black/85 backdrop-blur-md rounded-xl shadow-xl border border-stone-200 dark:border-white/20 overflow-hidden" data-id="element-947">
                    <div className="py-2" data-id="element-948">
                      <Link to="/help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-stone-50 dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-949">
                        Calculator & Quiz
                      </Link>
                      <Link to="/compare" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-stone-50 dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-950">
                        {t('nav.compareMaterials')}
                      </Link>
                      <Link to="/diy-help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-stone-50 dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-951">
                        Shop by Project
                      </Link>
                      <Link to="/delivery-prep" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-stone-50 dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-952">
                        Delivery Prep Guide
                      </Link>
                      <Link to="/subcontractors" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-stone-50 dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-953">
                        Contractors Zone
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* About Dropdown */}
            <div className="relative" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)} data-id="element-954">
              <button className={`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`} data-id="element-955">
                {t('nav.about')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-956" />
              </button>
              <AnimatePresence data-id="element-957">
                {isAboutOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-white dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-958">
                    <Link to="/about" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-959">
                      About Us
                    </Link>
                    <Link to="/about#gallery" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                  setTimeout(() => {
                    const el = document.getElementById('gallery');
                    if (el) {
                      const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                      const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                      window.scrollTo({
                        top,
                        behavior: 'smooth'
                      });
                    }
                  }, 400);
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-960">
                      Project Gallery
                    </Link>
                    <Link to="/local-vs-national" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-961">
                      Why Shop Local
                    </Link>
                    <Link to="/#faq" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:text-brand-orange transition-colors py-1.5`} data-id="element-962">
                      Da FAQs
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-963">
              Contact
            </Link>
            <Link to="/track" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-964">
              Track Order
            </Link>

            <div className="hidden md:flex items-center space-x-6" data-id="element-965">
              <div className="flex items-center space-x-3" data-id="element-966">
                <Link to="/delivery-zones" onClick={collapseCard} aria-label="Delivery Zones" title="Delivery Zones" className="relative w-9 h-9 rounded-full flex items-center justify-center text-stone-800 dark:text-white hover:text-brand-orange transition-colors group" data-id="element-967">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-968">
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#5c5c5c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-969">
                      <circle cx="12" cy="12" r="10" fill="none" data-id="element-970" />
                    </svg>
                  </div>
                  <motion.div className="absolute inset-1 pointer-events-none" animate={{
                  rotate: 360
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear'
                }} style={{
                  background: isDark ? 'conic-gradient(from 0deg, rgba(255,255,255,0.5) 0deg, rgba(255,255,255,0) 90deg, transparent 360deg)' : 'conic-gradient(from 0deg, rgba(41,37,36,0.5) 0deg, rgba(41,37,36,0) 90deg, transparent 360deg)',
                  WebkitMaskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  maskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  borderRadius: '9999px'
                }} data-id="element-971" />
                </Link>
                <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-972" />
              </div>
              <div className="h-6 w-px bg-stone-200 dark:bg-white/20" data-id="element-973" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className={`relative flex items-center gap-2 transition-all ${totalItems > 0 ? 'pl-2.5 pr-3.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 hover:bg-stone-300/80 dark:hover:bg-white/15 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-974">
                <div className="relative" data-id="element-975">
                  <Truck className="w-5 h-5" data-id="element-976" />
                  <AnimatePresence data-id="element-977">
                    {totalItems > 0 && <motion.span initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-978">
                        {totalItems}
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <AnimatePresence data-id="element-979">
                  {totalItems > 0 && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="text-sm font-bold whitespace-nowrap overflow-hidden" data-id="element-980">
                      ${totalPrice.toLocaleString()}
                    </motion.span>}
                </AnimatePresence>
              </button>
              <LanguageToggle data-id="element-981" />
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-3" data-id="element-982">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-983" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className={`relative flex items-center gap-1.5 transition-all ${totalItems > 0 ? 'pl-2 pr-2.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-984">
              <div className="relative" data-id="element-985">
                <Truck className="w-5 h-5" data-id="element-986" />
                <AnimatePresence data-id="element-987">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-988">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </div>
              {totalItems > 0 && <span className="text-xs font-bold whitespace-nowrap" data-id="element-989">
                  ${totalPrice.toLocaleString()}
                </span>}
            </button>
            <LanguageToggle data-id="element-990" />

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-id="element-991">
              {isOpen ? <X className="h-6 w-6" data-id="element-992" /> : <Menu className="h-6 w-6" data-id="element-993" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-994">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-995" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-white dark:bg-black/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-996">
              <div className="flex justify-between items-center p-6" data-id="element-997">
                <img src="https://cdn.magicpatterns.com/uploads/pv38EKqBFfU3b2ryMtdaxz/NEW_GRAVEL_LOGO_bw.png" alt="253Gravel" className="h-10 w-auto" style={{
              filter: isDark ? undefined : 'brightness(0)'
            }} data-id="element-998" />
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" aria-label="Close menu" data-id="element-999">
                  <X className="w-6 h-6" data-id="element-1000" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-1001">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-1002">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-1003">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1004">
                      <ChevronDown className="w-5 h-5" data-id="element-1005" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-1006">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1007">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-1008">
                          {categories.map(cat => <div key={cat.name} data-id="element-1009">
                              <p className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-2 text-right" data-id="element-1010">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-1011">
                                {cat.products.map((prod, prodIdx) => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(cat.name, prodIdx);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-1012">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 pt-3 border-t border-stone-200 dark:border-white/10" data-id="element-1013">
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-brand-orange/10 hover:bg-brand-orange/20 border border-brand-orange/30 rounded-lg p-3 transition-colors" data-id="element-1014">
                            <div className="text-right flex-1" data-id="element-1015">
                              <span className="block text-brand-orange font-bold text-sm" data-id="element-1016">
                                Contractor Ordering
                              </span>
                              <span className="block text-stone-500 dark:text-stone-300 text-xs mt-0.5" data-id="element-1017">
                                Bulk pricing & volume discounts
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-brand-orange ml-3" data-id="element-1018" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-1019">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1020">
                    Resources
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-1021" />
                  </button>
                  <AnimatePresence data-id="element-1022">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1023">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1024">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1025">
                            Calculator & Quiz
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1026">
                            Compare Materials
                          </Link>
                          <Link to="/diy-help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1027">
                            Shop by Project
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1028">
                            Delivery Prep Guide
                          </Link>
                          <Link to="/subcontractors" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1029">
                            Contractors Zone
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Mobile About Menu - Collapsible */}
                <div data-id="element-1030">
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1031">
                    About
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} data-id="element-1032" />
                  </button>
                  <AnimatePresence data-id="element-1033">
                    {mobileAboutOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1034">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1035">
                          <Link to="/about" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1036">
                            About Us
                          </Link>
                          <Link to="/about#gallery" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                      setTimeout(() => {
                        const el = document.getElementById('gallery');
                        if (el) {
                          const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                          const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                          window.scrollTo({
                            top,
                            behavior: 'smooth'
                          });
                        }
                      }, 400);
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1037">
                            Project Gallery
                          </Link>
                          <Link to="/local-vs-national" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1038">
                            Why Shop Local
                          </Link>
                          <Link to="/#faq" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1039">
                            Da FAQs
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1040">
                  Contact
                </Link>
                <Link to="/track" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1041">
                  Track Order
                </Link>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1042">
                  Delivery Zones
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}